/**
 * A combinação de condições que não pode casar nada.
 *
 * ## O silêncio que isto quebra
 *
 * As condições COMBINADAS exigem todas ao mesmo tempo. Repetir o mesmo campo
 * com "igual a" ali é pedir `status = Aberto E status = Pendente` — nenhum
 * chamado tem dois status. O filtro passa a devolver zero, e a tela diz
 * "Nenhum ticket encontrado", exatamente igual a quando de fato não há nada.
 *
 * Medido em produção em 16/10/2026: **quatro** kanbans de equipe estavam
 * assim, e ninguém sabia dizer desde quando. Não era descuido de quem criou —
 * era a única coisa que a tela deixava fazer, porque não havia "É um de" e o
 * filtro só tem um balde de "ou". O operador novo tirou a necessidade de
 * repetir; isto aqui tira o silêncio.
 *
 * ## O que NÃO é erro, e por que a regra é estreita
 *
 * - **"Diferente de" repetido é correto e comum**: `status ≠ Fechado E
 *   status ≠ Cancelado E status ≠ Resolvido` é como dois dos kanbans
 *   escrevem "não finalizado", e funciona.
 * - **No grupo INDEPENDENTE, repetir é o jeito certo**: lá as condições são
 *   "pelo menos uma", então `serviço = X OU serviço = Y` é exatamente o que
 *   se quer.
 * - **"É um de" repetido** seria interseção de listas: esquisito, mas pode
 *   casar. Não é impossível, então não entra.
 *
 * Só a igualdade repetida dentro do grupo "e" é impossível por construção, e
 * é só dela que a tela reclama. Avisar demais ensina a ignorar o aviso.
 */

const GRUPO_COMBINADO = 'combined';

const ehCombinada = (condicao) => (condicao?.logic_group || GRUPO_COMBINADO) !== 'independent';

/**
 * Os campos repetidos com "igual a" nas condições combinadas.
 *
 * @param {Array} condicoes  as condições padrão do filtro
 * @returns {string[]} os `field_name` que nunca vão casar, sem repetição
 */
export const camposQueNuncaCasam = (condicoes) => {
    const porCampo = {};

    (Array.isArray(condicoes) ? condicoes : []).forEach((c) => {
        if (!c || c.operator !== 'equals' || !ehCombinada(c)) return;
        if (!c.field_name) return;

        porCampo[c.field_name] = (porCampo[c.field_name] || 0) + 1;
    });

    return Object.keys(porCampo).filter((campo) => porCampo[campo] > 1);
};

/**
 * O recado para a tela, já com o nome que a pessoa vê e a saída.
 *
 * Devolve `null` quando não há nada a dizer -- aviso que aparece à toa é
 * aviso que se aprende a ignorar.
 *
 * @param {Array} condicoes
 * @param {(campo: string) => string} rotuloDoCampo  resolve "status_id" -> "Status"
 */
export const avisoDeCondicaoQueNuncaCasa = (condicoes, rotuloDoCampo) => {
    const campos = camposQueNuncaCasam(condicoes);

    if (campos.length === 0) return null;

    const nomes = campos.map((c) => (typeof rotuloDoCampo === 'function' ? rotuloDoCampo(c) : c));
    const lista = nomes.join(', ');
    const umSo = nomes.length === 1;

    return umSo
        ? `${lista} aparece mais de uma vez com "Igual a" nas condições combinadas, que exigem todas ao mesmo tempo — nenhum chamado atende, e o filtro não vai mostrar nada. Use "É um de" e escolha os valores de uma vez.`
        : `${lista} aparecem mais de uma vez com "Igual a" nas condições combinadas, que exigem todas ao mesmo tempo — nenhum chamado atende, e o filtro não vai mostrar nada. Use "É um de" e escolha os valores de uma vez.`;
};
